import { Minus, Plus, ReceiptText, UsersRound, WalletCards } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import './split-payment.css'

type TableItem = { product_name:string; quantity:number; total:number }
type SplitMode = 'free'|'people'|'items'

type Props = {
  items:TableItem[]
  remaining:number
  amount:string
  onAmountChange:(value:string)=>void
  onReferenceChange:(value:string)=>void
}

const money = new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'})

export function SplitPaymentControls({items,remaining,amount,onAmountChange,onReferenceChange}:Props){
  const [mode,setMode]=useState<SplitMode>('free')
  const [people,setPeople]=useState(2)
  const [shares,setShares]=useState(1)
  const [selected,setSelected]=useState<Record<string,number>>({})

  const rawItemsTotal=useMemo(()=>items.reduce((sum,item)=>sum+Number(item.total||0),0),[items])
  const remainingRatio=rawItemsTotal>0?remaining/rawItemsTotal:1

  const selectedItems=useMemo(()=>items.map(item=>{
    const quantity=Math.min(Number(item.quantity||0),selected[item.product_name]||0)
    const unit=Number(item.quantity||0)>0?Number(item.total||0)/Number(item.quantity):0
    const adjustedUnit=unit*remainingRatio
    return {...item,selectedQuantity:quantity,adjustedUnit,selectedTotal:quantity*adjustedUnit}
  }).filter(item=>item.selectedQuantity>0),[items,selected,remainingRatio])

  const selectedTotal=useMemo(()=>selectedItems.reduce((sum,item)=>sum+item.selectedTotal,0),[selectedItems])

  useEffect(()=>{
    if(mode==='free'){
      onReferenceChange('Divisão: valor livre')
      return
    }
    if(mode==='people'){
      const safePeople=Math.max(2,people)
      const safeShares=Math.min(Math.max(1,shares),safePeople)
      const value=Math.min(remaining,Number(((remaining/safePeople)*safeShares).toFixed(2)))
      onAmountChange(value.toFixed(2))
      onReferenceChange(`Divisão: ${safeShares} de ${safePeople} pessoa(s) · cota ${money.format(remaining/safePeople)}`)
      return
    }
    const value=Math.min(remaining,Number(selectedTotal.toFixed(2)))
    onAmountChange(value>0?value.toFixed(2):'')
    const detail=selectedItems.map(item=>`${item.selectedQuantity}x ${item.product_name}`).join(', ')
    onReferenceChange(detail?`Divisão por itens: ${detail}`:'Divisão por itens')
  },[mode,people,shares,selectedTotal,selectedItems,remaining,onAmountChange,onReferenceChange])

  function changeMode(next:SplitMode){
    setMode(next)
    setSelected({})
    if(next==='free')onAmountChange(remaining.toFixed(2))
    if(next==='people'){
      setPeople(2);setShares(1)
      onAmountChange((remaining/2).toFixed(2))
    }
    if(next==='items')onAmountChange('')
  }

  function changeItem(name:string,max:number,delta:number){
    setSelected(current=>{
      const next=Math.min(max,Math.max(0,(current[name]||0)+delta))
      return {...current,[name]:next}
    })
  }

  return <div className="split-payment-box">
    <div className="split-payment-title"><div><strong>Como deseja dividir?</strong><small>Escolha o valor desta cobrança sem alterar a comanda.</small></div><span>{money.format(remaining)} em aberto</span></div>
    <div className="split-mode-buttons">
      <button type="button" className={mode==='free'?'active':''} onClick={()=>changeMode('free')}><WalletCards size={17}/> Valor livre</button>
      <button type="button" className={mode==='people'?'active':''} onClick={()=>changeMode('people')}><UsersRound size={17}/> Por pessoas</button>
      <button type="button" className={mode==='items'?'active':''} onClick={()=>changeMode('items')}><ReceiptText size={17}/> Por itens</button>
    </div>

    {mode==='people'&&<div className="split-people-grid">
      <label>Total de pessoas<input type="number" min="2" max="50" value={people} onChange={event=>{const value=Math.max(2,Number(event.target.value)||2);setPeople(value);setShares(current=>Math.min(current,value))}}/></label>
      <label>Quantas cotas pagar agora<input type="number" min="1" max={people} value={shares} onChange={event=>setShares(Math.min(people,Math.max(1,Number(event.target.value)||1)))}/></label>
      <div className="split-result"><span>Valor por pessoa</span><strong>{money.format(remaining/Math.max(2,people))}</strong><small>Cobrança atual: {money.format(Number(amount)||0)}</small></div>
    </div>}

    {mode==='items'&&<div className="split-items-list">
      {items.map(item=>{
        const qty=selected[item.product_name]||0
        const unit=Number(item.quantity)>0?Number(item.total)/Number(item.quantity):0
        return <div className="split-item-row" key={item.product_name}>
          <div><strong>{item.product_name}</strong><small>{Number(item.quantity)} disponível(is) · {money.format(unit)} cada</small></div>
          <div className="split-item-control"><button type="button" onClick={()=>changeItem(item.product_name,Number(item.quantity),-1)} disabled={!qty}><Minus size={14}/></button><b>{qty}</b><button type="button" onClick={()=>changeItem(item.product_name,Number(item.quantity),1)} disabled={qty>=Number(item.quantity)}><Plus size={14}/></button></div>
        </div>
      })}
      <div className="split-items-total"><span>Itens selecionados</span><strong>{money.format(selectedTotal)}</strong></div>
      {Math.abs(remainingRatio-1)>.001&&<small className="split-warning">O valor dos itens foi ajustado proporcionalmente ao saldo atual da mesa, considerando pagamentos parciais, descontos, acréscimos e taxa de serviço.</small>}
    </div>}

    {mode==='free'&&<div className="split-free-note">Digite abaixo qualquer valor até o saldo total da mesa. Ideal para pagamentos parciais ou quando cada pessoa informa quanto deseja pagar.</div>}
  </div>
}
